import * as Sentry from "@sentry/nextjs";
import { useContentLink, useContentSingletonFallback, useContentSingletons, usePageContent, useXcoreCore } from "@robe/client";
import { WebLoginParams, WebLoginError, WebLoginErrorType, WebUserConfirmationResendError, NotFoundError, sentryLogger } from "@robe/core";
import { Box, Button, Flex, Link, Stack, Typography, useModal } from "@xcorejs/ui";
import { ErrorAnimatedIcon, LoadingAnimatedIcon, SuccessAnimatedIcon } from "../Icons/AnimatedIcon";
import { userLogout } from "components/Layout/LoggedBar";
import { Checkbox, FormControl, Input } from "design-system/xcore/Form";
import { Field, FieldProps, Form, Formik, FormikHelpers } from "formik";
import { useState } from "react";
import { useUser } from "hooks/useUser";
import { useLayout } from "xcore";
import { bool, object, string } from "yup";
import { useRevalidateCache } from "../../pages/_app";
import { ActivationPage, UserLoginPage } from "../../xcore/types";
import RobeLoader from "../RobeLoader";
import Richtext from "../../design-system/Richtext";
import { useRouter } from "next/router";
import { LostPasswordModal } from "./LostPasswordModal";

type LoginStatus = "success" | "error" | null;

const UserLoginComponent = () => {
  const core = useXcoreCore();
  const { locale, pathname } = useRouter();
  const { stringify, general, localize } = useLayout();
  const [lostPassword] = useModal(LostPasswordModal);
  const [close] = useModal();
  const ssrUserLoginPage = usePageContent<UserLoginPage>();
  const [ssrActivationPage] = useContentSingletons<[ActivationPage]>();
  const { data: activationPage, isLoading: activationPageIsLoading } = useContentSingletonFallback<ActivationPage>(
    "activationPage",
    ssrActivationPage
  );
  const { data: userLoginPage, isLoading: userLoginPageIsLoading } = useContentSingletonFallback<UserLoginPage>(
    "userLoginPage",
    ssrUserLoginPage,
    {
      includeValues: true,
    }
  );

  const { user } = useUser();
  const { as } = useContentLink();
  const [resendState, setResendState] = useState<null | "success" | "error" | string>(null);
  const [loginStatus, setLoginStatus] = useState<LoginStatus>(null);
  const revalidate = useRevalidateCache();
  const [keepLogin, setKeepLogin] = useState<boolean>(false);
  const [isUserNotActivatedError, setIsUserNotActivatedError] = useState<boolean>(false);

  const userLoginSchema = object().shape({
    email: string().required(stringify(general.values.requiredField)).email(stringify(general.values.validEmail)),
    password: string().required(stringify(general.values.requiredField)),
    remember: bool(),
  });

  const handleAfterLoginResponse = (status: LoginStatus, helpers: FormikHelpers<WebLoginParams>) => {
    helpers.setSubmitting(false);
    setLoginStatus(status);

    setTimeout(() => {
      setLoginStatus(null);
      status === "success" && close();
    }, 3000);
  };

  const userLogin = async (values: WebLoginParams, helpers: FormikHelpers<WebLoginParams>) => {
    try {
      const res = await core.web.login(values);

      if (res) {
        handleAfterLoginResponse("success", helpers);

        Sentry.setUser({
          id: res.id,
          email: res.email,
          ...(res.firstName &&
            res.lastName && {
            username: `${res.firstName} ${res.lastName}`,
          }),
        });

        revalidate();

        return;
      }

      handleAfterLoginResponse("error", helpers);
    } catch (error) {
      handleAfterLoginResponse("error", helpers);
      if (error instanceof NotFoundError) {
        helpers.setFieldError("email", stringify(general.values.userNameNF));

        return;
      }

      if (error instanceof WebLoginError) {
        switch (error.detail) {
          case WebLoginErrorType.WrongPassword:
            helpers.setFieldError("password", stringify(general.values.wrongPass));
            break;
          case WebLoginErrorType.NotActiveUser:
            setIsUserNotActivatedError(true);
        }

        return;
      }

      sentryLogger({
        error,
        message: "Unexpected error occurred during login",
        context: {
          user,
        },
      });

      helpers.setFieldError("email", "Unknown error has occurred. Try again later.");
    }
  };

  const resendActivation = async (email: string) => {
    const activationPageUrl = activationPage ? as(activationPage, { locale }) : null;
    if (activationPageUrl) {
      try {
        const res = await core.web.userConfirmationResend({
          email,
          activationPageUrl,
          locale: locale ?? "en",
        });

        if (res) {
          setResendState("success");
          setTimeout(() => setResendState(null), 3000);

          return;
        }

        setResendState("error");
        setTimeout(() => setResendState(null), 3000);
      } catch (error) {
        setTimeout(() => setResendState(null), 3000);

        if (error instanceof WebUserConfirmationResendError) {
          switch (error.detail) {
            case "InvalidToken":
              setResendState("Invalid token");
              break;
            case "TokenAlreadyUsed":
              setResendState("Token already used");
              break;
            case "UserAlreadyConfirmed":
              setResendState("User already confirmed");
              break;
          }

          return;
        }

        sentryLogger({
          error,
          message: "Unexpected error occurred during user activation resend",
          context: {
            user,
          },
        });

        setResendState("error");
      }
    }
  };

  const initialValues: WebLoginParams = { email: "", password: "", remember: false };

  return !userLoginPageIsLoading && userLoginPage ? (
    <>
      <Typography variant="h3" as="div" textAlign="center">
        {stringify(userLoginPage.values.userLoginHeading)}
      </Typography>

      {loginStatus === "success" ? (
        <>
          {localize(userLoginPage.values.userLoginIntro) && (
            <Box mt="1rem">
              <Richtext value={userLoginPage.values.userLoginIntro} _paragraph={{ textAlign: "center" }} />
            </Box>
          )}
          <Flex flexDirection="column" alignItems="center" mt="3rem">
            <SuccessAnimatedIcon />
          </Flex>
        </>
      ) : user ? (
        <Flex alignItems="center" flexDirection="column">
          {stringify(userLoginPage.values.userLoginLogoutText) && (
            <Typography mt="1rem">{stringify(userLoginPage.values.userLoginLogoutText)}</Typography>
          )}

          <Button mt="3rem" onClick={userLogout(core, revalidate)}>
            {stringify(userLoginPage.values.userLoginLogout)}
          </Button>
        </Flex>
      ) : (
        <>
          {localize(userLoginPage.values.userLoginIntro) && (
            <Box mt="1rem">
              <Richtext value={userLoginPage.values.userLoginIntro} _paragraph={{ textAlign: "center" }} />
            </Box>
          )}
          <Box width="100%" maxWidth="41rem" mx="auto" mt="3rem">
            <Formik initialValues={initialValues} onSubmit={userLogin} validationSchema={userLoginSchema}>
              {({ setFieldValue, values }) => (
                <Form>
                  <Stack direction="column" gap="3rem" wrapItems>
                    <Field name="email">
                      {({ field, meta }: FieldProps) => (
                        <FormControl
                          labelText={stringify(userLoginPage.values.userLoginEmailLabel)}
                          context="isRequired"
                          errorContext={meta.error && meta.touched ? "isInvalid" : "normal"}
                          {...(meta.touched && { error: meta.error })}
                          width="100%"
                        >
                          <Input inputSize="md" width="100%" type="text" {...field} />
                        </FormControl>
                      )}
                    </Field>
                    <Box>
                      <Field name="password">
                        {({ field, meta }: FieldProps) => (
                          <FormControl
                            labelText={stringify(userLoginPage.values.userLoginPasswordLabel)}
                            context="isRequired"
                            errorContext={meta.error && meta.touched ? "isInvalid" : "normal"}
                            {...(meta.touched && { error: meta.error })}
                            width="100%"
                          >
                            <Input inputSize="md" width="100%" type="password" {...field} />
                          </FormControl>
                        )}
                      </Field>
                      <Flex mt="2rem" justifyContent="center">
                        <Link
                          variant="underline"
                          onClick={() => {
                            if (pathname !== "/user/login") {
                              close();
                            }
                            lostPassword();
                          }}
                        >
                          {stringify(userLoginPage.values.userLoginForgottenPass)}
                        </Link>
                      </Flex>
                    </Box>

                    <Checkbox
                      checked={keepLogin}
                      onChange={(e: any) => {
                        setKeepLogin((p) => !p);
                        setFieldValue("remember", e.target.checked);
                      }}
                    >
                      <Typography>{stringify(userLoginPage.values.preserveLogin)}</Typography>
                    </Checkbox>

                    {isUserNotActivatedError && (
                      <>
                        <Typography color="robe" textAlign="center" fontWeight="bold">
                          {stringify(general.values.userNotActivated)}
                        </Typography>
                        <Flex justifyContent="center" mt="1rem">
                          {resendState === "success" ? (
                            <SuccessAnimatedIcon />
                          ) : resendState === "error" ? (
                            <ErrorAnimatedIcon />
                          ) : typeof resendState === "string" ? (
                            <Typography color="robe" textAlign="center">
                              {resendState}
                            </Typography>
                          ) : userLoginPageIsLoading || activationPageIsLoading ? (
                            <LoadingAnimatedIcon />
                          ) : (
                            <Link variant="underline" fontWeight={400} onClick={() => resendActivation(values.email)}>
                              {stringify(userLoginPage.values.userLoginResendActivation)}
                            </Link>
                          )}
                        </Flex>
                      </>
                    )}
                    <Flex justifyContent="center">
                      <Button display="flex" type="submit">
                        {stringify(userLoginPage.values.userLoginButton)}
                      </Button>
                    </Flex>
                  </Stack>
                </Form>
              )}
            </Formik>
          </Box>
        </>
      )}
    </>
  ) : (
    <Flex justifyContent="center" alignItems="center" width="100%">
      <RobeLoader />
    </Flex>
  );
};

export default UserLoginComponent;
